import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';
import type { DragSelectionDelegate, DragSelectionHitTester } from '../../directives/drag-selection';
import { TableViewDataSource } from './table-view.data-source';
import { VirtualScrollComponent, VirtualScrollDelegate } from '../virtual-scroll';

export interface IndexPath {
  section: number;
  row: number;
}

@Component({
    selector: 'ts-table-view',
    templateUrl: './table-view.component.html',
    styleUrls: ['./table-view.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class TableViewComponent<R = unknown, H = unknown> implements OnInit, OnDestroy {
  @Input() public rowNumeralTemplate: TemplateRef<unknown>;
  @Input() public headerTemplate: TemplateRef<unknown>;
  @Input() public rowTemplate: TemplateRef<unknown>;
  @Input() public loading: boolean;
  @Input() public scrollThreshold = 0;
  @Input() public paddingNodes = 1;
  @Input() public locationRow = -1;
  @Input() public stickyHeader = true;
  @Input() public dataSource: TableViewDataSource<R, H>;
  @Input() public scrollDelegate: VirtualScrollDelegate;

  @Input() public dragSelectionEnabled = false;
  @Input() public dragSelectionHitTester: DragSelectionHitTester<unknown> | null = null;
  @Input() public dragSelectionDelegate: DragSelectionDelegate<unknown> | null = null;

  @ViewChild(VirtualScrollComponent) public virtualScroll?: VirtualScrollComponent;

  public rowsPerSection: number[] = [];
  public rowGap = 0;
  public rowHeight: number;
  public sectionRows: number[] = [];
  public numberOfRows = 0;
  public currentLocationRow = 0;
  public currentSectionIndex = 0;

  private sectionCache = new Map<number, number>();
  private cacheLimit = 1000;
  private dataSourceSubscriptions: Subscription[] = [];

  constructor(private readonly cdr: ChangeDetectorRef) {}

  public ngOnInit(): void {
    this.subscribeToDataSource();
  }

  public ngOnDestroy(): void {
    this.dataSourceSubscriptions.forEach((s) => s.unsubscribe());
    this.sectionCache.clear();
  }

  /**
   * Determines if the given rowIndex corresponds to a section header.
   */
  public isSectionRow(rowIndex: number): boolean {
    return this.sectionRows.includes(rowIndex);
  }

  /**
   * Returns the section index for the given rowIndex.
   */
  public getSectionIndexForRow(rowIndex: number): number {
    return this.sectionRows.indexOf(rowIndex);
  }

  /**
   * Returns the section containing the given rowIndex using a binary search
   * and caches the result to avoid redundant lookups.
   */
  public getSectionContainingRow(rowIndex: number): number {
    // Return cached result if available
    if (this.sectionCache.has(rowIndex)) return this.sectionCache.get(rowIndex);

    let low = 0;
    let high = this.sectionRows.length - 1;

    // Binary search for the correct section
    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      if (this.sectionRows[mid] <= rowIndex) {
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }

    const sectionIndex = high;

    // Cache the result
    this.sectionCache.set(rowIndex, sectionIndex);
    this.enforceCacheLimit(); // Ensure the cache doesn't grow too large

    return sectionIndex;
  }

  /**
   * Returns the IndexPath (section and row) for the given rowIndex.
   */
  public getIndexPathForRow(rowIndex: number): IndexPath {
    const sectionIndex = this.getSectionContainingRow(rowIndex);

    if (sectionIndex < 0 || sectionIndex >= this.sectionRows.length) {
      return { section: -1, row: -1 }; // Invalid IndexPath
    }

    return {
      section: sectionIndex,
      row: rowIndex - this.sectionRows[sectionIndex] - 1,
    };
  }

  public updateCurrentRow(rowIndex: number) {
    this.currentLocationRow = rowIndex;
    this.currentSectionIndex = this.getSectionContainingRow(rowIndex);
  }

  /**
   * Returns the flat row index (as seen by the virtual-scroll viewport) for a
   * given IndexPath. Inverse of {@link getIndexPathForRow}.
   */
  public getRowForIndexPath(indexPath: IndexPath): number {
    const sectionHeaderRow = this.sectionRows[indexPath.section];
    if (sectionHeaderRow === undefined) return -1;
    return sectionHeaderRow + indexPath.row + 1;
  }

  public scrollIndexPathIntoViewIfNeeded(indexPath: IndexPath): void {
    const rowIndex = this.getRowForIndexPath(indexPath);
    if (rowIndex < 0) return;
    this.virtualScroll?.scrollRowIntoViewIfNeeded(rowIndex);
  }

  private subscribeToDataSource(): void {
    this.dataSourceSubscriptions.forEach((s) => s.unsubscribe());
    this.dataSourceSubscriptions = [];

    this.dataSourceSubscriptions.push(
      this.dataSource.getRowsPerSection().subscribe((rowsPerSection) => {
        this.rowsPerSection = rowsPerSection;
        this.calculateSectionsAndRows();
        this.cdr.markForCheck();
      }),
      this.dataSource.getRowHeight().subscribe((rowHeight) => {
        this.rowHeight = rowHeight;
        this.cdr.markForCheck();
      }),
      this.dataSource.getRowGap().subscribe((rowGap) => {
        this.rowGap = rowGap;
        this.cdr.markForCheck();
      })
    );
  }

  /**
   * Efficiently calculates the section start indices and total number of rows.
   */
  private calculateSectionsAndRows() {
    this.sectionCache.clear();

    if (!this.rowsPerSection || this.rowsPerSection.length === 0) {
      this.sectionRows = [];
      this.numberOfRows = 0;
      return;
    }

    this.sectionRows = new Array(this.rowsPerSection.length);
    this.sectionRows[0] = -1;
    this.numberOfRows = this.rowsPerSection.length; // One row for each section header

    for (let i = 0; i < this.rowsPerSection.length; i++) {
      this.numberOfRows += this.rowsPerSection[i]; // Add rows for each section
      this.sectionRows[i + 1] = this.sectionRows[i] + this.rowsPerSection[i] + 1; // Account for section header
    }
  }

  /**
   * Limits the cache size to the specified limit by removing the oldest entries.
   */
  private enforceCacheLimit() {
    if (!(this.sectionCache.size > this.cacheLimit)) return;

    const firstKey = this.sectionCache.keys().next().value;
    this.sectionCache.delete(firstKey);
  }
}
